import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  Plus,
  Search,
  Edit2,
  Trash2,
  Filter,
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  FileText,
} from 'lucide-react';
import {
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Button,
  Badge,
  Modal,
  ModalFooter,
  Input,
  Select,
  StatCard,
} from '@azyntor-labs/nexus-ui';
import { questionBanksApi } from '@/api/endpoints';
import { QuestionBank } from '@/types';
import { format } from 'date-fns';
import { toast } from '@/utils/toast';
import { Link } from 'react-router-dom';

export const QuestionBankManagement = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [subjectFilter, setSubjectFilter] = useState<string>('');

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState<QuestionBank | null>(null);
  const [duplicateName, setDuplicateName] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subject: '',
    grade_level: '',
    tags: [] as string[],
    is_public: false,
  });

  // Fetch question banks
  const { data: banksResponse, isLoading } = useQuery({
    queryKey: ['question-banks', { page, per_page: perPage, subject: subjectFilter }],
    queryFn: () => questionBanksApi.list({
      page,
      per_page: perPage,
      subject: subjectFilter || undefined
    }),
  });

  // Correctly extract banks array from nested response
  const banks = Array.isArray(banksResponse?.data?.data?.data)
    ? banksResponse.data.data.data
    : [];
  const total = banksResponse?.data?.data?.total || 0;
  const totalPages = Math.ceil(total / perPage) || 1;

  // Filter banks by search term (only if search is active)
  const filteredBanks = searchTerm
    ? banks.filter((bank: QuestionBank) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          bank.name?.toLowerCase().includes(searchLower) ||
          bank.description?.toLowerCase().includes(searchLower) ||
          bank.subject?.toLowerCase().includes(searchLower)
        );
      })
    : banks;

  // Calculate stats
  const totalQuestions = banks.reduce((sum: number, bank: QuestionBank) => sum + (bank.question_count || 0), 0);
  const publicBanks = banks.filter((b: QuestionBank) => b.is_public).length;

  // Create bank mutation
  const createBankMutation = useMutation({
    mutationFn: (data: Partial<QuestionBank>) => questionBanksApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['question-banks'] });
      toast.success('Question bank created successfully');
      setIsAddModalOpen(false);
      resetForm();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to create question bank';
      toast.error(message);
    },
  });

  // Update bank mutation
  const updateBankMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<QuestionBank> }) =>
      questionBanksApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['question-banks'] });
      toast.success('Question bank updated successfully');
      setIsEditModalOpen(false);
      setSelectedBank(null);
      resetForm();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to update question bank';
      toast.error(message);
    },
  });

  // Delete bank mutation
  const deleteBankMutation = useMutation({
    mutationFn: (id: string) => questionBanksApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['question-banks'] });
      toast.success('Question bank deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedBank(null);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to delete question bank';
      toast.error(message);
    },
  });

  // Duplicate bank mutation
  const duplicateBankMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      questionBanksApi.duplicate(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['question-banks'] });
      toast.success('Question bank duplicated successfully');
      setIsDuplicateModalOpen(false);
      setSelectedBank(null);
      setDuplicateName('');
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to duplicate question bank';
      toast.error(message);
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      subject: '',
      grade_level: '',
      tags: [],
      is_public: false,
    });
  };

  const handleAddBank = () => {
    setIsAddModalOpen(true);
    resetForm();
  };

  const handleEditBank = (bank: QuestionBank) => {
    setSelectedBank(bank);
    setFormData({
      name: bank.name,
      description: bank.description,
      subject: bank.subject,
      grade_level: bank.grade_level,
      tags: bank.tags || [],
      is_public: bank.is_public,
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteBank = (bank: QuestionBank) => {
    setSelectedBank(bank);
    setIsDeleteModalOpen(true);
  };

  const handleDuplicateBank = (bank: QuestionBank) => {
    setSelectedBank(bank);
    setDuplicateName(`${bank.name} (Copy)`);
    setIsDuplicateModalOpen(true);
  };

  const handleSubmitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.subject) {
      toast.error('Please fill in all required fields');
      return;
    }
    createBankMutation.mutate(formData);
  };

  const handleSubmitUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBank) return;
    updateBankMutation.mutate({ id: selectedBank.id, data: formData });
  };

  const handleConfirmDelete = () => {
    if (!selectedBank) return;
    deleteBankMutation.mutate(selectedBank.id);
  };

  const handleConfirmDuplicate = () => {
    if (!selectedBank || !duplicateName) return;
    duplicateBankMutation.mutate({ id: selectedBank.id, name: duplicateName });
  };

  // Get unique subjects for filter
  const uniqueSubjects = Array.from(new Set(banks.map((b: QuestionBank) => b.subject))).filter(Boolean);
  const subjectOptions = [
    { value: '', label: 'All Subjects' },
    ...uniqueSubjects.map(subject => ({ value: subject, label: subject }))
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Question Bank Management</h1>
          <p className="text-white/50 mt-1">Organize and manage your question libraries</p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={handleAddBank}>
          Create Question Bank
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Banks"
          value={total}
          icon={<BookOpen />}
          color="teal"
        />
        <StatCard
          title="Total Questions"
          value={totalQuestions}
          icon={<FileText />}
          color="green"
        />
        <StatCard
          title="Public Banks"
          value={publicBanks}
          icon={<Eye />}
          color="teal"
        />
        <StatCard
          title="Subjects"
          value={uniqueSubjects.length}
          icon={<BookOpen />}
          color="orange"
        />
      </div>

      {/* Filters and Search */}
      <Card>
        <div className="p-4 border-b border-white/[0.06]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                placeholder="Search question banks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select
              options={subjectOptions}
              value={subjectFilter}
              onChange={(e) => {
                setSubjectFilter(e.target.value);
                setPage(1);
              }}
            />
            <div className="flex items-center text-sm text-white/50">
              <Filter className="w-4 h-4 mr-2" />
              Showing {filteredBanks.length} of {total} question banks
            </div>
          </div>
        </div>

        {/* Question Banks Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-white/60">Loading question banks...</p>
          </div>
        ) : filteredBanks.length > 0 ? (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader className="w-[30%]">Name</TableHeader>
                  <TableHeader className="w-[12%]">Subject</TableHeader>
                  <TableHeader className="w-[10%]">Grade</TableHeader>
                  <TableHeader className="w-[8%]">Questions</TableHeader>
                  <TableHeader className="w-[10%]">Visibility</TableHeader>
                  <TableHeader className="w-[10%]">Created</TableHeader>
                  <TableHeader className="w-[20%]">Actions</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBanks.map((bank: QuestionBank) => (
                  <TableRow key={bank.id}>
                    <TableCell className="w-[30%]">
                      <div className="max-w-md">
                        <div className="font-medium text-white truncate">{bank.name}</div>
                        <div className="text-sm text-white/50 truncate">
                          {bank.description}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-white/60 w-[12%]">{bank.subject}</TableCell>
                    <TableCell className="text-white/60 w-[10%]">{bank.grade_level || '-'}</TableCell>
                    <TableCell className="w-[8%]">
                      <Badge variant="info" size="sm">
                        {bank.question_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="w-[10%]">
                      <Badge variant={bank.is_public ? 'success' : 'secondary'} size="sm">
                        {bank.is_public ? 'Public' : 'Private'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-white/50 w-[10%]">
                      {format(new Date(bank.created_at), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell className="w-[20%]">
                      <div className="flex items-center space-x-1">
                        <Link to={`/teacher/question-banks/${bank.id}`}>
                          <Button variant="ghost" size="sm" leftIcon={<Eye className="w-3 h-3" />}>
                            View
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Edit2 className="w-3 h-3" />}
                          onClick={() => handleEditBank(bank)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Copy className="w-3 h-3" />}
                          onClick={() => handleDuplicateBank(bank)}
                          className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                        >
                          Duplicate
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Trash2 className="w-3 h-3" />}
                          onClick={() => handleDeleteBank(bank)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="p-4 border-t border-white/[0.06] flex items-center justify-between">
              <div className="text-sm text-white/50">
                Page {page} of {totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<ChevronLeft className="w-4 h-4" />}
                  onClick={() => {
                    setPage((p) => Math.max(1, p - 1));
                    setSearchTerm(''); // Clear search when changing pages
                  }}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setPage((p) => Math.min(totalPages, p + 1));
                    setSearchTerm(''); // Clear search when changing pages
                  }}
                  disabled={page === totalPages}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-white/20 mx-auto mb-2" />
            <p className="text-white/40 mb-3">No question banks found</p>
            {searchTerm ? (
              <p className="text-sm text-white/30">Try adjusting your search or filters</p>
            ) : (
              <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={handleAddBank}>
                Create Your First Question Bank
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Add Question Bank Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Create Question Bank"
        size="lg"
      >
        <form onSubmit={handleSubmitCreate}>
          <div className="space-y-4">
            <Input
              label="Name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Physics Grade 10"
            />
            <Input
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this question bank"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Subject"
                required
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="e.g., Physics, Mathematics"
              />
              <Input
                label="Grade Level"
                value={formData.grade_level}
                onChange={(e) => setFormData({ ...formData, grade_level: e.target.value })}
                placeholder="e.g., 10, 11-12"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_public"
                checked={formData.is_public}
                onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-teal-500 focus:ring-teal-500/20 focus:ring-offset-0"
              />
              <label htmlFor="is_public" className="text-sm font-medium text-white/70">
                Make this question bank public (visible to other teachers)
              </label>
            </div>
          </div>
          <ModalFooter>
            <Button type="button" variant="secondary" onClick={() => setIsAddModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createBankMutation.isPending}>
              {createBankMutation.isPending ? 'Creating...' : 'Create Question Bank'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Edit Question Bank Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Question Bank"
        size="lg"
      >
        <form onSubmit={handleSubmitUpdate}>
          <div className="space-y-4">
            <Input
              label="Name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <Input
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Subject"
                required
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              />
              <Input
                label="Grade Level"
                value={formData.grade_level}
                onChange={(e) => setFormData({ ...formData, grade_level: e.target.value })}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit_is_public"
                checked={formData.is_public}
                onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-teal-500 focus:ring-teal-500/20 focus:ring-offset-0"
              />
              <label htmlFor="edit_is_public" className="text-sm font-medium text-white/70">
                Make this question bank public
              </label>
            </div>
          </div>
          <ModalFooter>
            <Button type="button" variant="secondary" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateBankMutation.isPending}>
              {updateBankMutation.isPending ? 'Updating...' : 'Update Question Bank'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Question Bank"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-white/70">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-white">{selectedBank?.name}</span>? This action cannot be undone.
          </p>
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-sm text-red-300">
              Warning: All questions in this bank will be permanently deleted.
            </p>
          </div>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={deleteBankMutation.isPending}
          >
            {deleteBankMutation.isPending ? 'Deleting...' : 'Delete Question Bank'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Duplicate Question Bank Modal */}
      <Modal
        isOpen={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
        title="Duplicate Question Bank"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-white/70">
            Create a copy of <span className="font-semibold text-white">{selectedBank?.name}</span>
          </p>
          <Input
            label="New Question Bank Name"
            required
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            placeholder="Enter name for the duplicated bank"
          />
          <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-blue-300">
              All questions from the original bank will be copied to the new bank.
            </p>
          </div>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setIsDuplicateModalOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDuplicate}
            disabled={duplicateBankMutation.isPending || !duplicateName}
          >
            {duplicateBankMutation.isPending ? 'Duplicating...' : 'Duplicate'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
